/**
 * #2031 商品のバリエーション（味・サイズ・トッピング）。
 *
 * 規則の正は **サーバ** (`services/pay/api/services/menu_variation.py`)。ここは画面が
 * 「選べるもの」を出し、「選び終わったか」を見るためのものでしかない — 細工した POST は
 * サーバが弾く。
 *
 * 形:
 * - 店が「選択グループ」を 1 回だけ作る (`Store.variationGroups`)
 * - 商品はそれを参照する (`MenuItem.variationGroupIds`)
 *
 * 焼き鳥 10 種類でも「味」を作るのは 1 回で、後から「味噌」を足すのも 1 か所の編集で済む。
 * 売り切れは選択肢に持つので**店全体で共有**される（味玉は材料なので、切れたら全商品で切れる）。
 *
 * ## 金額
 *
 * 有料の選択 (味玉 +100 円) は**サーバが店の定義から足す**。画面が送る `price` は**本体だけ**で、
 * ここの計算は客に見せる見積もりでしかない (定義はサーバの docstring「金額」)。
 * カートの行は `price` = 本体 + 追加、`optionsPrice` = そのうちの追加分、で持つ
 * (注文後にサーバが返す明細と同じ意味)。送るときに本体へ戻す (`lib/cartPayload.ts`)。
 */

/** #2156 客が読む訳。商品の `translations` と同じ形 (`{ ロケール: { キー: 文字 } }`)。 */
export type VariationTranslations = Record<string, Record<string, string> | undefined>

export interface VariationOption {
  id: string
  label: string
  /** #2156 `{ en: { label: 'Salt' }, ... }`。日本語は `label` が正なので持たない。 */
  translations?: VariationTranslations
  /** 追加料金 (円・0 以上)。0 や未設定は無料の選択 (塩／たれ) で、画面に何も足さない。 */
  priceDelta?: number
  soldOut?: boolean
}

export interface VariationGroup {
  id: string
  name: string
  /** #2156 `{ en: { name: 'Flavor' }, ... }`。日本語は `name` が正なので持たない。 */
  translations?: VariationTranslations
  /** 'single' = 1つだけ選ぶ (味・サイズ) / 'multi' = いくつでも選ぶ (トッピング) */
  select: 'single' | 'multi'
  required: boolean
  options: VariationOption[]
}

/** 客が選んだもの。`{ グループ ID: [選択肢 ID, ...] }`。サーバへはこの形で送る。 */
export type VariationSelection = Record<string, string[]>

/** サーバが検算して注文に焼き込んだ形。**表示はこちらを使う**（店が定義を直しても変わらない）。 */
export interface VariationSnapshot {
  groupId: string
  name: string
  optionIds: string[]
  labels: string[]
  /** #2156 サーバが注文時に焼き込んだ訳。**日本語の `name` / `labels` は残る** —
   *  厨房の伝票とレシートは店員が読む。日本語の注文・訳の無い選択肢では付かない。 */
  nameLocalized?: string
  labelsLocalized?: string[]
  /** `labels` と同じ並びの追加料金。全部 0 のときはサーバが持たせない。 */
  priceDeltas?: number[]
}

/**
 * #2156 客に見せる 1 つの文字列。**訳 → 日本語**。
 *
 * 商品名の解決 (selectLocalizedName) と同じ順。バリエーションには英語の欄が無いので、
 * 実際は訳が無ければ日本語に落ちる。
 */
export function localizedText(
  entry: { translations?: VariationTranslations } | null | undefined,
  key: string,
  fallback: string,
  locale: string,
): string {
  if (!entry || !locale || locale === 'ja') return fallback
  const translated = entry.translations?.[locale]?.[key]
  return (translated ?? '').trim() || fallback
}

/** 追加料金の表示。無料の選択には何も付けない。 */
export function priceTag(delta?: number | null): string {
  return delta && delta > 0 ? `+¥${delta.toLocaleString()}` : ''
}

const withTag = (label: string, delta?: number | null): string => {
  const tag = priceTag(delta)
  return tag ? `${label}(${tag})` : label
}

/** 選んだ内容の追加料金の合計 (1 個あたり)。**見積もり** — 請求額はサーバが決める。 */
export function selectionPrice(groups: VariationGroup[], selection?: VariationSelection | null): number {
  if (!selection) return 0
  return groups.reduce((sum, g) => {
    const picked = selection[g.id] ?? []
    return sum + g.options
      .filter((o) => picked.includes(o.id))
      .reduce((s, o) => s + Math.max(0, o.priceDelta ?? 0), 0)
  }, 0)
}

/** 商品に付いている選択グループ。**店側の並び順**で返す（サーバの groups_for_item と同じ）。 */
export function groupsForItem(
  store: { variationGroups?: VariationGroup[] | null } | null | undefined,
  item: { variationGroupIds?: string[] | null } | null | undefined,
): VariationGroup[] {
  const refs = new Set(item?.variationGroupIds ?? [])
  if (refs.size === 0) return []
  return (store?.variationGroups ?? []).filter((g) => refs.has(g.id))
}

/**
 * 必須なのに**選べる選択肢が 1 つも無い**グループ。あればその商品は注文できない。
 * 商品自体は売り切れになっていないので、画面で言わないと客には故障に見える。
 */
export function blockedGroups(groups: VariationGroup[]): VariationGroup[] {
  return groups.filter((g) => g.required && g.options.every((o) => o.soldOut))
}

/** 必須のグループを全部選び終わったか。カートに入れるボタンを押せるかの判定。 */
export function isSelectionComplete(groups: VariationGroup[], selection: VariationSelection): boolean {
  return groups.every((g) => !g.required || (selection[g.id]?.length ?? 0) > 0)
}

/** 選択肢を 1 つ押したときの次の選択。single は入れ替え、multi は付け外し。 */
export function toggleOption(
  group: VariationGroup,
  selection: VariationSelection,
  optionId: string,
): VariationSelection {
  const current = selection[group.id] ?? []
  let next: string[]
  if (group.select === 'single') {
    // 任意のグループは**もう一度押すと外せる**。必須は外せない（選び直すだけ）。
    next = current.includes(optionId) && !group.required ? [] : [optionId]
  } else {
    next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId]
  }
  const out = { ...selection }
  if (next.length) out[group.id] = next
  else delete out[group.id]
  return out
}

/**
 * カートの行を分ける鍵。**同じ商品でも選んだ内容が違えば別の行**
 * （「もも・塩」×2 は 1 行、「もも・たれ」は別行）。並び順で別物にならないよう並べ替える。
 * 選んでいない商品の鍵は商品 ID そのもの — バリエーションを使わない店では今までと同じ。
 */
export function cartLineId(menuItemId: string, selection?: VariationSelection | null): string {
  const groupIds = Object.keys(selection ?? {}).filter((g) => (selection?.[g]?.length ?? 0) > 0).sort()
  if (groupIds.length === 0) return menuItemId
  const parts = groupIds.map((g) => `${g}=${[...(selection?.[g] ?? [])].sort().join(',')}`)
  return `${menuItemId}#${parts.join('|')}`
}

/** 注文前（カート）の表示。店の定義から名前を引く。例: `味: たれ / トッピング: 味玉` */
export function selectionText(
  groups: VariationGroup[],
  selection?: VariationSelection | null,
  locale = 'ja',
): string {
  if (!selection) return ''
  return groups
    .map((g) => {
      const picked = selection[g.id] ?? []
      const labels = g.options
        .filter((o) => picked.includes(o.id))
        .map((o) => withTag(localizedText(o, 'label', o.label, locale), o.priceDelta))
      return labels.length ? `${localizedText(g, 'name', g.name, locale)}: ${labels.join(', ')}` : ''
    })
    .filter(Boolean)
    .join(' / ')
}

/**
 * 注文後の表示。サーバが焼き込んだスナップショットから。
 *
 * #2156 `localized=true` で、サーバが焼いた訳を出す。**引き直さない** — 店が定義を
 * 直しても、売った時点の表示のまま。
 */
export function snapshotText(
  variations?: VariationSnapshot[] | null,
  localized = false,
): string {
  return (variations ?? [])
    .filter((v) => v.labels?.length)
    .map((v) => {
      const name = (localized && v.nameLocalized) || v.name
      const labels = (localized && v.labelsLocalized?.length === v.labels.length)
        ? v.labelsLocalized : v.labels
      return `${name}: ${labels.map((label, i) => withTag(label, v.priceDeltas?.[i])).join(', ')}`
    })
    .join(' / ')
}

/**
 * #2156 明細に出すバリエーションの 2 行。**客の言語が主・日本語が従。**
 *
 * 商品名 (`lib/itemName.ts` の `itemNameLines`) と同じ形にしてある。日本語を残すのが
 * 肝で、**客が画面を店員に見せたときに双方が読める**。片方だけにすると、店員か客の
 * どちらかが読めなくなる。
 *
 * 日本語で注文したとき・訳が無いときは 1 行（今までと同じ）。
 */
export function variationLines(
  item: { variations?: VariationSnapshot[] | null; variationText?: string },
): { primary: string; secondary?: string } {
  const ja = snapshotText(item.variations) || item.variationText || ''
  const localized = snapshotText(item.variations, true)
  if (!localized || localized === ja) return { primary: ja }
  // **カテゴリー名は繰り返さない**（「味」「ご飯の量」は見出しであって作り分けの中身ではない）。
  // 2 行目は日本語の選択肢だけ — 店員が読むのはそこで、厨房には日本語の伝票が別に届く。
  const jaOptions = (item.variations ?? [])
    .filter((v) => v.labels?.length)
    .flatMap((v) => v.labels.map((label, i) => withTag(label, v.priceDeltas?.[i])))
    .join(' / ')
  return { primary: localized, secondary: jaOptions || undefined }
}

/**
 * 明細 1 行に出すバリエーション。**注文後はサーバが焼き込んだ方を優先する** —
 * 店が定義を直しても、売った時点の名前が出る。注文前 (カート) は手元の表示用を使う。
 */
export function variationLineOf(
  item: { variations?: VariationSnapshot[] | null; variationText?: string },
  localized = false,
): string {
  return snapshotText(item.variations, localized) || item.variationText || ''
}

/** 「もう一度注文」でカートに戻すとき、スナップショットから選択を組み直す。 */
export function selectionFromSnapshot(variations?: VariationSnapshot[] | null): VariationSelection | undefined {
  const out: VariationSelection = {}
  for (const v of variations ?? []) {
    if (v.optionIds?.length) out[v.groupId] = [...v.optionIds]
  }
  return Object.keys(out).length ? out : undefined
}
